import { paginatedQuery } from 'lib-shared-api-utils';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import { PresenceOrganizationWorkloadSettings } from './types/presence.types';
import {
  ReadAgentPresenceApiArg,
  ReadAgentPresenceApiResponse,
  ReadAgentsPresenceApiResponse,
  ReadAgentsPresenceV2ApiArg,
  ReadAgentsPresenceV2ApiResponse,
  ReadAgentsPresenceV2TotalApiArg,
  ReadAgentsPresenceV2TotalApiResponse,
  ReadOrganizationWorkloadSettingsApiResponse,
  UpdateOrganizationWorkloadSettingsApiResponse,
  UpdatePresenceChannelApiArg,
  UpdatePresenceChannelApiResponse,
  UpdatePresenceMediaApiArg,
  UpdatePresenceMediaApiResponse,
  UpdatePresenceStatusApiArg,
  UpdatePresenceStatusApiResponse,
  UpdateSimultaneousRingApiArg,
  UpdateSimultaneousRingApiResponse,
} from './types/presenceApi.types';
import {
  transformReadAgentPresenceResponse,
  transformReadAgentsPresenceResponse,
  transformReadAgentsPresenceV2Response,
} from './utils/transformResponse';

export const presenceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readAgentPresence: builder.query<
      ReadAgentPresenceApiResponse,
      ReadAgentPresenceApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        path: `presence/${id}`,
      }),
      transformResponse: transformReadAgentPresenceResponse,
    }),
    readAgentsPresence: builder.query<ReadAgentsPresenceApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'presence',
      }),
      transformResponse: transformReadAgentsPresenceResponse,
    }),
    updateSimultaneousRing: builder.mutation<
      UpdateSimultaneousRingApiResponse,
      UpdateSimultaneousRingApiArg
    >({
      query: ({ id, enabled }) => ({
        version: 1,
        method: 'PUT',
        path: id
          ? `presence/simultaneous_ring/${id}`
          : 'presence/simultaneous_ring',
        body: { enabled },
      }),
    }),
    updatePresenceStatus: builder.mutation<
      UpdatePresenceStatusApiResponse,
      UpdatePresenceStatusApiArg
    >({
      query: ({ status: presence }) => ({
        version: 1,
        method: 'PUT',
        path: 'presence',
        body: { presence },
      }),
    }),
    updatePresenceMedia: builder.mutation<
      UpdatePresenceMediaApiResponse,
      UpdatePresenceMediaApiArg
    >({
      query: (body) => ({
        version: 1,
        method: 'PUT',
        path: 'presence/media',
        body,
      }),
    }),
    updatePresenceChannel: builder.mutation<
      UpdatePresenceChannelApiResponse,
      UpdatePresenceChannelApiArg
    >({
      query: (body) => ({
        version: 1,
        method: 'PUT',
        path: 'presence/channel',
        body,
      }),
    }),
    readOrganizationWorkloadSettings: builder.query<
      ReadOrganizationWorkloadSettingsApiResponse,
      void
    >({
      query: () => ({
        version: 1,
        path: 'presence/workload_settings/organization',
      }),
    }),
    updateOrganizationWorkloadSettings: builder.mutation<
      UpdateOrganizationWorkloadSettingsApiResponse,
      PresenceOrganizationWorkloadSettings
    >({
      query: (body) => ({
        version: 1,
        method: 'PUT',
        path: 'presence/workload_settings/organization',
        body,
      }),
      async onQueryStarted(
        { load_limits, allowed_offers_while_on_call },
        { dispatch, queryFulfilled },
      ) {
        const patchReadOrganizationWorkloadSettings = dispatch(
          presenceApi.util.updateQueryData(
            'readOrganizationWorkloadSettings',
            undefined,
            (draft) => {
              const mutableData = draft.data as DeepWritable<typeof draft.data>;

              mutableData.load_limits = load_limits;
              mutableData.allowed_offers_while_on_call =
                allowed_offers_while_on_call;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadOrganizationWorkloadSettings.undo();
        }
      },
    }),
    readAgentsPresenceV2: builder.query<
      ReadAgentsPresenceV2ApiResponse,
      ReadAgentsPresenceV2ApiArg
    >({
      query: (arg) =>
        paginatedQuery(
          arg,
          ({
            status,
            hasAssignedConversations: has_assigned_conversations,
            favouriteAgentIds,
          }) => ({
            version: 2,
            path: 'presence',
            params: {
              status,
              has_assigned_conversations,
              ...(favouriteAgentIds && {
                favourites: favouriteAgentIds.join(','),
              }),
            },
          }),
        ),
      transformResponse: transformReadAgentsPresenceV2Response,
    }),
    readAgentsPresenceV2Total: builder.query<
      ReadAgentsPresenceV2TotalApiResponse,
      ReadAgentsPresenceV2TotalApiArg
    >({
      query: () => ({
        version: 2,
        path: 'presence',
      }),
    }),
  }),
});
